import type {
  FollowedUser,
  GetCoachesResponse,
  GetDeleteUser,
  GetUserActivitiesParams,
  GetUserActivitiesResponse,
  GetUserByIdResponse,
  GetUserData,
  GetUsersParams,
  GetUsersResponse,
  Message,
  RequestUpdateUserSettings,
  ResponseUserSettings,
} from "src/service/UsersApi/type";
import { serverApi } from "src/service/serverApi";
import type { ResultResponse } from "src/types/api";

export const usersApi = serverApi.injectEndpoints({
  endpoints: (builder) => {
    return {
      // aws fetch test
      awsTest: builder.query<Message, void>({
        query: () => {
          return { method: "get", url: "/" };
        },
      }),

      // 他のユーザーページでアバターのいいねを削除
      deleteUserAvatarGood: builder.mutation<ResultResponse, string>({
        invalidatesTags: ["UserSettings", "User"],
        query: (userId) => {
          return { method: "delete", url: `users/${userId}/good` };
        },
      }),

      //ユーザーのフォロー解除
      deleteUserFollow: builder.mutation<GetDeleteUser, { id: string }>({
        invalidatesTags: [
          "MypageFollowUsers",
          "UserSettings",
          "UsersFollowing",
          "User",
        ],

        query: (userId) => {
          return {
            body: userId.id,
            method: "delete",
            url: `users/${userId.id}/follow`,
          };
        },

        async onQueryStarted(args, { dispatch }) {
          try {
            dispatch(
              usersApi.util.updateQueryData("getUsers", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = false;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getFollower", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = false;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getFollowings", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = false;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getCoachsAdmins", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = false;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData(
                "getRecommendUsers",
                {},
                (draft) => {
                  draft.data.map(async (data) => {
                    if (data.id === args.id) {
                      data.isFollowed = false;
                    }
                  });
                },
              ),
            );
          } catch {}
        },
      }),
      // ユーザーの活動記録
      getActivities: builder.query<
        GetUserActivitiesResponse,
        GetUserActivitiesParams & { userId: string }
      >({
        providesTags: ["UserActivities"],
        query: (params) => {
          return {
            method: "get",
            params: { cursor: params.cursor, limit: params.limit },
            url: `users/${params.userId}/activities`,
          };
        },
      }),
      getCoachsAdmins: builder.query<GetUsersResponse, GetUsersParams>({
        // cursorが変わったら再フェッチ
        forceRefetch({ currentArg, previousArg }) {
          return (
            (currentArg?.cursor !== previousArg?.cursor &&
              currentArg?.cursor !== "") ||
            currentArg?.keywords !== previousArg?.keywords
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersCoach"],
        query: (params) => {
          return { method: "get", params, url: "users" };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getCoaches: builder.query<
        GetCoachesResponse,
        {
          cursor?: string;
          limit?: string;
          keywords?: string;
          periodId?: string;
        }
      >({
        providesTags: ["UsersCoach"],
        forceRefetch({ currentArg, previousArg }) {
          return (
            (currentArg?.cursor !== previousArg?.cursor &&
              currentArg?.cursor !== "") ||
            currentArg?.keywords !== previousArg?.keywords
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const updatedData = [...currentCache.data];

            newItems.data.forEach((newItem) => {
              if (existingIds.has(newItem.id)) {
                // Update existing item
                const existingIndex = updatedData.findIndex(
                  (item) => item.id === newItem.id,
                );
                if (existingIndex !== -1) {
                  updatedData[existingIndex] = newItem;
                }
              } else {
                // Add new item
                updatedData.push(newItem);
              }
            });

            currentCache.data = updatedData;
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        query: (params) => {
          return {
            method: "get",
            params: {
              cursor: params.cursor,
              limit: params.limit,
              keywords: params.keywords,
              periodId: params.periodId,
            },
            url: "users/coaches",
          };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getFollower: builder.query<GetUsersResponse, GetUserActivitiesParams>({
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor == undefined || arg.cursor == "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersFollower"],
        query: (params) => {
          return {
            method: "get",
            params: { cursor: params.cursor, limit: params.limit },
            url: "users/follower",
          };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getFollowings: builder.query<GetUsersResponse, GetUserActivitiesParams>({
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersFollowing"],
        query: (params) => {
          return {
            method: "get",
            params: { cursor: params.cursor, limit: params.limit },
            url: "users/following",
          };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      // 自分の活動記録
      getMyActivities: builder.query<
        GetUserActivitiesResponse,
        GetUserActivitiesParams
      >({
        providesTags: ["MyActivities"],
        query: (params) => {
          return {
            method: "get",
            params,
            url: "users/activities",
          };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      // マイページ用のフォローユーザー取得
      getMyPageFollowUser: builder.query<
        GetUserData[],
        { cursor?: string; limit?: string }
      >({
        providesTags: ["MypageFollowUsers"],
        query: (params) => {
          return {
            method: "get",
            params,
            url: `users/following`,
          };
        },
        transformResponse: (response: GetUsersResponse) => {
          const getRandomFollowUsers = (
            data: GetUserData[],
            count: number,
          ): GetUserData[] => {
            if (data.length === 0 || count <= 0) {
              return [];
            }

            let randomValues: GetUserData[] = [];
            const availableIndices: number[] = Array.from(
              Array(data.length).keys(),
            );

            while (randomValues.length < count && availableIndices.length > 0) {
              const randomIndex = Math.floor(
                Math.random() * availableIndices.length,
              );
              const selectedValue = data[availableIndices[randomIndex]];
              randomValues = [...randomValues, selectedValue];
              availableIndices.splice(randomIndex, 1);
            }

            return randomValues;
          };
          const selectedFollowUsers = getRandomFollowUsers(
            response?.data || [],
            2,
          );
          return selectedFollowUsers;
        },
      }),
      getRecommendUsers: builder.query<
        GetUsersResponse,
        GetUserActivitiesParams
      >({
        providesTags: ["UsersRecommend"],
        query: (params) => {
          return {
            method: "get",
            params: { limit: params.limit },
            url: "users/recommend",
          };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getUser: builder.query<GetUserByIdResponse, string>({
        providesTags: ["User"],
        query: (userId) => {
          return { method: "get", url: `users/${userId}` };
        },
      }),

      getUsers: builder.query<GetUsersResponse, GetUsersParams>({
        // cursorが変わったら再フェッチ
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersAll"],
        query: (params) => {
          return { method: "get", params, url: "users" };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      getAdminUsers: builder.query<GetUsersResponse, GetUsersParams>({
        // cursorが変わったら再フェッチ
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersAll"],
        query: (params) => {
          return { method: "get", params, url: "users" };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      // 自分の情報を取得
      getUserSettings: builder.query<ResponseUserSettings, void>({
        providesTags: ["UserSettings", "User"],
        query: () => {
          return { method: "get", url: "users/settings" };
        },
      }),
      patchPasswordChange: builder.mutation<
        { success: boolean; message: string },
        { oldPassword: string; newPassword: string }
      >({
        invalidatesTags: ["User"],
        query: (body) => {
          return {
            body,
            method: "PATCH",
            url: `auth/password`,
          };
        },
      }),
      // 他のユーザーページでアバターのいいねする
      postUserAvatarGood: builder.mutation<
        {
          id: string;
          createdAt: Date;
          recevedUserId: string;
          userId: string;
        },
        string
      >({
        invalidatesTags: ["UserSettings", "User", "Points", "Missions"],
        query: (userId) => {
          return { method: "post", url: `users/${userId}/good` };
        },
      }),
      postEmailChange: builder.mutation<
        {
          message: string;
          success: string;
        },
        { email: string }
      >({
        query: (body) => {
          return {
            body: body,
            method: "post",
            url: `auth/verify_email`,
          };
        },
      }),
      //ユーザーをフォロー
      putUserFollow: builder.mutation<FollowedUser, { id: string }>({
        invalidatesTags: [
          "MypageFollowUsers",
          "Points",
          "Missions",
          "UsersFollowing",
          "User",
        ],

        async onQueryStarted(args, { dispatch }) {
          try {
            dispatch(
              usersApi.util.updateQueryData("getUsers", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = true;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData(
                "getRecommendUsers",
                {},
                (draft) => {
                  draft.data.map(async (data) => {
                    if (data.id === args.id) {
                      data.isFollowed = true;
                    }
                  });
                },
              ),
            );
            dispatch(
              usersApi.util.updateQueryData("getFollower", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = true;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getFollowings", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = true;
                  }
                });
              }),
            );
            dispatch(
              usersApi.util.updateQueryData("getCoachsAdmins", {}, (draft) => {
                draft.data.map(async (data) => {
                  if (data.id === args.id) {
                    data.isFollowed = true;
                  }
                });
              }),
            );
          } catch {}
        },

        query: (userId) => {
          return {
            body: userId.id,
            method: "put",
            url: `users/${userId.id}/follow`,
          };
        },
      }),
      // 自分の情報を更新
      updateUserSettings: builder.mutation<
        ResponseUserSettings,
        RequestUpdateUserSettings
      >({
        invalidatesTags: ["UserSettings", "User"],
        query: (body) => {
          return {
            body,
            method: "PATCH",
            url: `users/settings`,
          };
        },
      }),
      adminUpdateUserSettings: builder.mutation<
        ResponseUserSettings,
        RequestUpdateUserSettings & { userId: string }
      >({
        invalidatesTags: ["UserSettings", "User", "UsersAdmin"],
        query: (body) => {
          return {
            body,
            method: "PATCH",
            url: `users/${body.userId}`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getAdminUsers", {}, (draft) => {
                draft.data.map((user) => {
                  if (user.id === args.userId) {
                    user.isCanAddMoreCourse = args.isCanAddMoreCourse || false;
                  }
                });
              }),
            );
          } catch {}
        },
      }),
      patchUpdateEmail: builder.mutation<
        { message: string; success: string },
        { token: string }
      >({
        invalidatesTags: ["UserSettings"],
        query: (body) => {
          return {
            body: body,
            method: "PATCH",
            url: `auth/update_email`,
          };
        },
      }),
      upgradeToAdmin: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { id: string }
      >({
        invalidatesTags: ["UsersCoach"],
        query: (params) => {
          return {
            method: "PATCH",
            url: `users/${params.id}/upgrade_to_admin`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getCoaches", {}, (draft) => {
                draft.data.forEach((coach) => {
                  if (coach.id === args.id) {
                    console.log(coach);
                    coach.isUpgradedRole = true;
                  }
                });
              }),
            );
          } catch {}
        },
      }),
      downgradeToCoach: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { id: string }
      >({
        invalidatesTags: ["UsersCoach"],
        query: (params) => {
          return {
            method: "PATCH",
            url: `users/${params.id}/downgrade_to_coach`,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              usersApi.util.updateQueryData("getCoaches", {}, (draft) => {
                draft.data.forEach((coach) => {
                  if (coach.id === args.id) {
                    coach.isUpgradedRole = false;
                  }
                });
              }),
            );
          } catch {}
        },
      }),
      getClassmates: builder.query<GetUsersResponse, GetUsersParams>({
        // cursorが変わったら再フェッチ
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.cursor !== previousArg?.cursor &&
            currentArg?.cursor !== ""
          );
        },
        // キャッシュのマージ
        merge: (currentCache, newItems, { arg }) => {
          if (arg.cursor === undefined || arg.cursor === "") {
            currentCache.data = newItems.data;
          } else {
            const existingIds = new Set(
              currentCache.data.map((item) => item.id),
            );
            const filteredNewItems = newItems.data.filter(
              (newItem) => !existingIds.has(newItem.id),
            );
            currentCache.data = currentCache.data.concat(filteredNewItems);
          }
          currentCache.pagination.endCursor = newItems.pagination.endCursor;
          currentCache.pagination.hasNextPage = newItems.pagination.hasNextPage;
        },
        providesTags: ["UsersClassmate"],
        query: (params) => {
          return { method: "get", params, url: "users" };
        },
        // 設定
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
      }),
      updateCoachPeriod: builder.mutation<
        {
          message: string;
          success: boolean;
        },
        { periodIds: string[]; userId: string }
      >({
        invalidatesTags: ["User"],
        query: (params) => {
          return {
            body: {
              periodIds: params.periodIds,
            },
            method: "PATCH",
            url: `users/${params.userId}/coach_periods`,
          };
        },
      }),
      withdrawal: builder.mutation<
        {
          message: string;

          success: boolean;
        },
        { userId: string; scheduledDeletionAt?: string }
      >({
        invalidatesTags: [
          "User",
          "UsersAll",
          "Course",
          "Period",
          "CoachSessions",
          "CoachSession",
          "Sessions",
          "Session",
          "UserSessions",
          "EligibleUsersForSession",
          "UsersClassmate",
          "UserRescheduleSessions",
        ],
        query: (params) => {
          return {
            body: params.scheduledDeletionAt
              ? { scheduledDeletionAt: params.scheduledDeletionAt }
              : {},
            method: "DELETE",
            url: `users/${params.userId}/withdrawal`,
          };
        },
      }),
      getPaymentSync: builder.mutation<void, void>({
        query: () => {
          return {
            method: "POST",
            url: `payment/sync`,
          };
        },
      }),
    };
  },
  overrideExisting: true,
});

export const {
  useAwsTestQuery,
  useDeleteUserAvatarGoodMutation,
  useDeleteUserFollowMutation,
  useGetActivitiesQuery,
  useGetAdminUsersQuery,
  useGetCoachesQuery,
  useGetCoachsAdminsQuery,
  useGetFollowerQuery,
  useGetFollowingsQuery,
  useGetMyActivitiesQuery,
  useGetMyPageFollowUserQuery,
  useGetRecommendUsersQuery,
  useGetUserQuery,
  useGetUserSettingsQuery,
  useGetUsersQuery,
  usePatchPasswordChangeMutation,
  usePostUserAvatarGoodMutation,
  usePostEmailChangeMutation,
  usePutUserFollowMutation,
  useUpdateUserSettingsMutation,
  usePatchUpdateEmailMutation,
  useUpgradeToAdminMutation,
  useDowngradeToCoachMutation,
  useGetClassmatesQuery,
  useAdminUpdateUserSettingsMutation,
  useUpdateCoachPeriodMutation,
  useWithdrawalMutation,
  useGetPaymentSyncMutation,
} = usersApi;
